import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SoftwareApplicationSchema, BreadcrumbSchema, HowToSchema, OrganizationSchema } from '@/components/SEOSchema';
import {
  FileText, Calculator, Check, ChevronDown, Shield, Zap, Euro,
  Users, TrendingUp, ArrowRight, BarChart3, Receipt, X, BookOpen,
  Archive, Video, Heart, QrCode, Clock,
} from 'lucide-react';
import { useCalEmbed, openCalModal } from '@/hooks/useCalEmbed';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Footer from '@/components/Footer';
import SeoClusterLinks from '@/components/SeoClusterLinks';
import { useTracking, useScrollTracking, useSectionTracking } from '@/hooks/useTracking';
import { useHreflang, HREFLANG_SETS } from '@/hooks/useHreflang';

const ICON_MAP = { FileText, Calculator, Check, Shield, Zap, Euro, Users, TrendingUp, BarChart3, Receipt, BookOpen, Archive, Heart, QrCode, Clock };

function TrustBadge({ icon, label }) {
  const Icon = ICON_MAP[icon] || Check;
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-emerald-600" />
      <span>{label}</span>
    </div>
  );
}

function FinalTrustBadge({ icon, label }) {
  const Icon = ICON_MAP[icon] || Check;
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </div>
  );
}

const COLOR_CLASSES = {
  emerald: { bg: "bg-emerald-100", text: "text-emerald-600", border: "border-t-emerald-500", check: "text-emerald-500" },
  blue:    { bg: "bg-blue-100",    text: "text-blue-600",    border: "border-t-blue-500",    check: "text-blue-500" },
  purple:  { bg: "bg-purple-100",  text: "text-purple-600",  border: "border-t-purple-500",  check: "text-purple-500" },
  orange:  { bg: "bg-orange-100",  text: "text-orange-600",  border: "border-t-orange-500",  check: "text-orange-500" },
  yellow:  { bg: "bg-yellow-100",  text: "text-yellow-600",  border: "border-t-yellow-500",  check: "text-yellow-500" },
  red:     { bg: "bg-red-100",     text: "text-red-600",     border: "border-t-red-500",     check: "text-red-500" },
};

export default function BuchhaltungTemplate({ cfg }) {
  const [openFaq, setOpenFaq] = useState(null);
  useCalEmbed();
  const { trackCTAClick, trackFAQOpen } = useTracking();
  useScrollTracking();

  const heroRef = useRef(null);
  const whyDifferentRef = useRef(null);
  const workflowRef = useRef(null);
  const legalRef = useRef(null);
  const comparisonRef = useRef(null);
  const faqRef = useRef(null);
  const finalCtaRef = useRef(null);
  const qrBillRef = useRef(null);

  useSectionTracking('hero', heroRef);
  useSectionTracking('why_different', whyDifferentRef);
  useSectionTracking('workflow', workflowRef);
  useSectionTracking('legal', legalRef);
  useSectionTracking('comparison', comparisonRef);
  useSectionTracking('faq', faqRef);
  useSectionTracking('final_cta', finalCtaRef);
  if (cfg.qrBillSection) useSectionTracking('qr_bill', qrBillRef);

  useHreflang({
    currentUrl: HREFLANG_SETS.buchhaltung[cfg.hreflangKey],
    alternates: HREFLANG_SETS.buchhaltung,
    xDefault: HREFLANG_SETS.buchhaltung.xDefault,
  });

  useEffect(() => {
    document.title = cfg.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', cfg.metaDescription);
    const ogTags = {
      'og:title': cfg.ogTitle, 'og:description': cfg.ogDescription, 'og:type': 'website',
      'og:url': cfg.ogUrl, 'og:site_name': 'ivoliq', 'og:locale': cfg.ogLocale,
      'twitter:card': 'summary_large_image', 'twitter:title': cfg.twitterTitle, 'twitter:description': cfg.twitterDescription,
    };
    Object.entries(ogTags).forEach(([prop, content]) => {
      const isOg = prop.startsWith('og:');
      const sel = isOg ? `meta[property="${prop}"]` : `meta[name="${prop}"]`;
      let tag = document.querySelector(sel);
      if (!tag) { tag = document.createElement('meta'); if (isOg) tag.setAttribute('property', prop); else tag.setAttribute('name', prop); document.head.appendChild(tag); }
      tag.setAttribute('content', content);
    });
  }, [cfg]);

  const toggleFaq = (index, question) => {
    setOpenFaq(openFaq === index ? null : index);
    if (openFaq !== index) trackFAQOpen(question);
  };

  const faqSchema = { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": cfg.faqs.map(faq => ({ "@type": "Question", "name": faq.question, "acceptedAnswer": { "@type": "Answer", "text": faq.answer } })) };

  return (
    <div className="min-h-screen bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <SoftwareApplicationSchema country={cfg.country} />
      <BreadcrumbSchema items={[{ name: "Home", url: "/" }, { name: "Buchhaltung Freelancer", url: "/" }, { name: cfg.breadcrumbLast }]} />
      <HowToSchema name={cfg.howToName} description={cfg.howToDescription} steps={cfg.howToSteps} />
      <OrganizationSchema />

      {/* Hero */}
      <section ref={heroRef} data-testid="hero-section" className="bg-gradient-to-b from-emerald-50 to-white pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            {cfg.heroTitle}{' '}<span className="text-emerald-600">| Einfach, rechtssicher, automatisiert</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">{cfg.heroSubtitle}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
            <Link to="/signup" onClick={() => trackCTAClick('hero', 'Jetzt kostenlos starten')} data-testid="hero-cta-button">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-10 py-7 text-lg shadow-lg">Jetzt kostenlos starten</Button>
            </Link>
            <Button size="lg" variant="outline" onClick={openCalModal} className="px-8 py-7 text-lg" data-testid="hero-demo-button">
              <Video className="h-5 w-5 mr-2" /> Demo buchen
            </Button>
          </div>
          <div className="mb-8">
            <a href="#workflow" className="text-emerald-600 hover:text-emerald-700 font-semibold underline inline-flex items-center gap-1">Wie es funktioniert <ArrowRight className="h-4 w-4" /></a>
          </div>
          <p className="text-sm text-gray-600 font-semibold mb-8">✓ Free Plan ohne Kreditkarte • ✓ 30 Tage Pro kostenlos testen</p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            {cfg.trustBadges.map((b, i) => <TrustBadge key={i} {...b} />)}
          </div>
        </div>
      </section>

      {/* Why Different */}
      <section ref={whyDifferentRef} data-testid="why-different-section" className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">{cfg.whyDifferentTitle}</h2>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            {cfg.whyDifferentParagraphs.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section id="workflow" ref={workflowRef} data-testid="workflow-section" className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Buchhaltung mit ivoliq: Der Freelancer-Workflow</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">{cfg.workflowSubtitle}</p>
          <div className="grid md:grid-cols-2 gap-8">
            {cfg.workflowCards.map((card, i) => {
              const cc = COLOR_CLASSES[card.color];
              const Icon = ICON_MAP[card.icon] || FileText;
              return (
                <Card key={i} className={`hover:shadow-lg transition-shadow border-t-4 ${cc.border}`}>
                  <CardContent className="p-8">
                    <div className={`h-14 w-14 ${cc.bg} rounded-xl flex items-center justify-center mb-5`}>
                      <Icon className={`h-7 w-7 ${cc.text}`} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{card.title}</h3>
                    <p className="text-gray-600 mb-4">{card.description}</p>
                    <ul className="space-y-2 text-gray-600 text-sm">
                      {card.items.map((item, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <Check className={`h-4 w-4 ${cc.check} mt-0.5 flex-shrink-0`} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link to="/signup" onClick={() => trackCTAClick('workflow', 'Jetzt kostenlos starten')} data-testid="workflow-cta-button">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-10 py-7 text-lg shadow-lg">Jetzt kostenlos starten</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Legal */}
      <section ref={legalRef} data-testid="legal-section" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">{cfg.legalTitle}</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">Die wichtigsten Pflichten auf einen Blick – verständlich erklärt.</p>
          <div className="space-y-6">
            {cfg.legalItems.map((item, i) => {
              const cc = COLOR_CLASSES[item.color];
              const Icon = ICON_MAP[item.icon] || BookOpen;
              return (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex items-start gap-5">
                  <div className={`h-12 w-12 ${cc.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`h-6 w-6 ${cc.text}`} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* QR-Bill (CH only) */}
      {cfg.qrBillSection && (
        <section ref={qrBillRef} data-testid="qr-bill-section" className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-8 md:p-12 border border-emerald-200">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <div className="h-24 w-24 bg-white rounded-2xl shadow-lg flex items-center justify-center">
                    <QrCode className="h-14 w-14 text-emerald-600" />
                  </div>
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">{cfg.qrBillSection.title}</h2>
                  <p className="text-gray-700 mb-4 leading-relaxed">{cfg.qrBillSection.description}</p>
                  <div className="grid sm:grid-cols-3 gap-4 mt-6">
                    {cfg.qrBillSection.benefits.map((b, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Check className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                        <span className="text-gray-700 text-sm font-medium">{b}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-gray-600 mt-4 text-sm">{cfg.qrBillSection.detail}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Comparison */}
      <section ref={comparisonRef} data-testid="comparison-section" className={`py-16 ${cfg.qrBillSection ? 'bg-gray-50' : ''}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">{cfg.comparisonTitle}</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">{cfg.comparisonSubtitle}</p>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Excel */}
            <Card className="border-2 border-gray-200 relative">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="h-14 w-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3"><FileText className="h-7 w-7 text-gray-500" /></div>
                  <h3 className="text-xl font-bold text-gray-900">Excel</h3>
                  <p className="text-sm text-gray-500 mt-1">Die Bastellösung</p>
                </div>
                <ul className="space-y-3 text-sm">
                  {cfg.excelCons.map((item, i) => (<li key={i} className="flex items-start gap-2 text-red-600"><X className="h-4 w-4 mt-0.5 flex-shrink-0" /><span>{item}</span></li>))}
                </ul>
              </CardContent>
            </Card>
            {/* Advisor */}
            <Card className="border-2 border-gray-200 relative">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="h-14 w-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3"><Users className="h-7 w-7 text-blue-500" /></div>
                  <h3 className="text-xl font-bold text-gray-900">{cfg.advisorLabel}</h3>
                  <p className="text-sm text-gray-500 mt-1">Der Experte</p>
                </div>
                <ul className="space-y-3 text-sm">
                  {cfg.advisorPros.map((item, i) => (<li key={i} className="flex items-start gap-2 text-emerald-600"><Check className="h-4 w-4 mt-0.5 flex-shrink-0" /><span>{item}</span></li>))}
                  {cfg.advisorCons.map((item, i) => (<li key={`c${i}`} className="flex items-start gap-2 text-red-600"><X className="h-4 w-4 mt-0.5 flex-shrink-0" /><span>{item}</span></li>))}
                </ul>
              </CardContent>
            </Card>
            {/* ivoliq */}
            <Card className="border-2 border-emerald-500 relative shadow-lg">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-4 py-1 rounded-full">EMPFOHLEN</div>
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="h-14 w-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-3"><Zap className="h-7 w-7 text-emerald-600" /></div>
                  <h3 className="text-xl font-bold text-gray-900">ivoliq</h3>
                  <p className="text-sm text-emerald-600 font-medium mt-1">{cfg.ivoliqLabel}</p>
                </div>
                <ul className="space-y-3 text-sm">
                  {cfg.ivoliqPros.map((item, i) => (<li key={i} className="flex items-start gap-2 text-emerald-600"><Check className="h-4 w-4 mt-0.5 flex-shrink-0" /><span>{item}</span></li>))}
                </ul>
              </CardContent>
            </Card>
          </div>
          <div className="mt-10 bg-emerald-50 rounded-xl p-6 border border-emerald-200 text-center">
            <p className="text-gray-700 text-lg"><strong>Unsere Empfehlung:</strong> {cfg.comparisonFazit}</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section ref={faqRef} data-testid="faq-section" className={`py-16 ${cfg.qrBillSection ? '' : 'bg-gray-50'}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">{cfg.faqTitle}</h2>
          <div className="space-y-4">
            {cfg.faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid={`faq-item-${index}`}>
                <button onClick={() => toggleFaq(index, faq.question)} style={{ display: "flex" }} className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors" data-testid={`faq-button-${index}`}>
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && <div className="px-6 pb-4 text-gray-600 leading-relaxed">{faq.answer}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className={`py-12 ${cfg.qrBillSection ? 'bg-gray-50' : ''}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600 mb-4">Auch interessant für dich:</p>
          <div className="flex flex-wrap justify-center gap-4">
            {cfg.internalLinks.map((link, i) => (
              <Link key={i} to={link.to} className="text-emerald-600 hover:text-emerald-700 underline">{link.label}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section ref={finalCtaRef} data-testid="final-cta-section" className="py-16 bg-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Buchhaltung, die sich nicht wie Buchhaltung anfühlt</h2>
          <p className="text-xl text-emerald-50 mb-8">{cfg.finalCtaSubtitle}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/signup" onClick={() => trackCTAClick('final_cta', 'Jetzt kostenlos testen')} data-testid="final-cta-button">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 px-10 py-7 text-lg shadow-2xl">Jetzt kostenlos testen</Button>
            </Link>
            <Button size="lg" variant="outline" onClick={openCalModal} className="border-white text-white hover:bg-emerald-700 px-8 py-7 text-lg" data-testid="final-demo-button">
              <Video className="h-5 w-5 mr-2" /> Demo buchen
            </Button>
          </div>
          <p className="text-sm text-emerald-100 mt-6">✓ Free Plan ohne Kreditkarte • ✓ 30 Tage Pro kostenlos testen</p>
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-emerald-100">
            {cfg.finalTrustBadges.map((b, i) => <FinalTrustBadge key={i} {...b} />)}
          </div>
        </div>
      </section>

      <SeoClusterLinks cluster="buchhaltung" country={cfg.country} />
      <Footer />
    </div>
  );
}
